import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import logo from '../assets/img/logo2.png';

const PasswordResetConfirmPage = () => {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!uid || !token) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-4">
        <Link to="/" className="mb-8">
          <img src={logo} alt="Dorito Stats" className="h-14" />
        </Link>
        <div className="w-full max-w-sm bg-ds-white border border-border-card rounded-card shadow-card p-8 text-center">
          <h1 className="font-display text-2xl font-black text-ds-text mb-2">
            Invalid link
          </h1>
          <p className="text-text-muted text-sm mb-6">
            This password reset link is invalid or has expired.
          </p>
          <Link
            to="/password-reset"
            className="text-green font-semibold text-sm hover:text-mustard transition-colors"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/auth/password-reset/confirm/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, token, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(true);
      } else {
        const msg = Array.isArray(data.error)
          ? data.error.join(' ')
          : data.error || 'Failed to reset password.';
        setError(msg);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-4">
        <Link to="/" className="mb-8">
          <img src={logo} alt="Dorito Stats" className="h-14" />
        </Link>
        <div className="w-full max-w-sm bg-ds-white border border-border-card rounded-card shadow-card p-8 text-center">
          <CheckCircle className="w-10 h-10 text-green mx-auto mb-4" />
          <h1 className="font-display text-2xl font-black text-ds-text mb-2">
            Password reset
          </h1>
          <p className="text-text-muted text-sm mb-6">
            Your password has been reset. You can now sign in with your new password.
          </p>
          <Link
            to="/login"
            className="inline-block bg-mustard text-forest font-extrabold text-sm px-5 py-2.5 rounded-lg shadow-mustard hover:bg-yellow-500 transition-colors"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-4">
      <Link to="/" className="mb-8">
        <img src={logo} alt="Dorito Stats" className="h-14" />
      </Link>

      <div className="w-full max-w-sm bg-ds-white border border-border-card rounded-card shadow-card p-8">
        <h1 className="font-display text-2xl font-black text-ds-text mb-6">
          Set new password
        </h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">
              New password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoFocus
              className="w-full border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-green transition-colors"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">
              Confirm new password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-green transition-colors"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p className="text-coral text-sm font-semibold">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-mustard text-forest font-extrabold text-sm py-2.5 rounded-lg shadow-mustard hover:bg-yellow-500 transition-colors disabled:opacity-60"
          >
            {loading ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetConfirmPage;
